// @ts-nocheck
import {
  Flex,
  Button,
  Text,
  Container,
  IconButton,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion"; // Import motion
import ChordIdentification from "components/exercises/lesson/ChordIdentification";
import IntervalIdentification from "components/exercises/lesson/IntervalIdentification";
import MultipleChoiceExercise from "components/exercises/lesson/MultipleChoiceExercise";
import NoteIdentification from "components/exercises/lesson/NoteIdentification";
import ScaleAscendingDescending from "components/exercises/lesson/ScaleAscendingDescending";
import UnsupportedExercise from "components/exercises/lesson/UnsupportedExercise";
import { useEffect, useRef, useState } from "react";
import { MusicExerciseGenerator } from "utils/exercises/MusicExerciseGenerator";
import {
  ExerciseDefinition,
  ExerciseType,
  ExercisePoints,
} from "utils/exercises/types";
import { useSearchParams } from "react-router-dom";
import { HiCheck, HiOutlineX } from "react-icons/hi";
import { FiFlag } from "react-icons/fi";
import { useSound } from "providers/SoundProvider";

const Landing: React.FC = () => {
  const { setIsAudioPlaying, isAudioPlaying } = useSound();
  const [searchParams] = useSearchParams();
  const jsonData = searchParams.get("data");
  const parsedData = jsonData ? JSON.parse(decodeURIComponent(jsonData)) : null;
  const [lessonCreated, setLessonCreated] = useState(false);
  const [localModuleType, setLocalModuleType] = useState("General Challenge");

  const [exercises, setExercises] = useState<ExerciseDefinition[]>([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const currentExercise = exercises[currentExerciseIndex];
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const currentExerciseRef = useRef<React.ReactElement | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (parsedData && !lessonCreated) {
      const { keyRoot, difficulty, sessionLength, moduleType } = parsedData; // Access parsed values directly
      createLesson(keyRoot, difficulty, sessionLength, moduleType);
      setLocalModuleType(moduleType);
      setLessonCreated(true); // Mark the lesson as created
    }
  }, [parsedData, lessonCreated]); // Add lessonCreated as a dependency

  useEffect(() => {
    if (currentExerciseIndex === 0 && isAudioPlaying) {
      setIsAudioPlaying(false);
    }
  }, [currentExerciseIndex, isAudioPlaying]);

  const createLesson = (
    keyRoot: number,
    difficulty: number,
    sessionLength: number,
    moduleType: string
  ) => {
    const generator = new MusicExerciseGenerator(
      {
        keyRoot: keyRoot,
        difficulty: difficulty || 1, // Default to difficulty 1 if not provided
        sessionLength: sessionLength || 15, // Default to 15 if not provided
      },
      moduleType
    );

    const generatedExercises = generator.generateSessionExercises();
    console.log("exercises", generatedExercises);
    setExercises(generatedExercises);
  };

  const checkAnswer = () => {
    if (currentExerciseRef.current) {
      const exerciseComponent = currentExerciseRef.current as any; // Assuming exercise components have a checkAnswer function
      if (exerciseComponent.checkAnswer) {
        exerciseComponent.checkAnswer();
      } else {
        console.warn(
          "Current exercise component does not have a checkAnswer function"
        );
      }
      setHasSubmitted(true);
    }
  };

  const handleSkip = () => {
    handleNextExercise();
  };

  const handleNextExercise = () => {
    if (currentExerciseIndex < exercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
    } else {
      setIsCorrect(null);
      setCurrentExerciseIndex(0); // Reset to the first exercise or close lesson
      //closeLesson();

      window.ReactNativeWebView &&
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            status: "closeMessage",
            message: "Lesson completed, closing lesson...",
          })
        );
    }
    setHasSubmitted(false);
    setIsCorrect(null); // Reset the correctness indicator
  };

  const handleExerciseResult = (correct: boolean) => {
    const points = ExercisePoints[currentExercise.type]; // Get the points for the current exercise type
    const awardedPoints = correct ? points.correct : points.incorrect; // Determine awarded points based on correctness

    window.ReactNativeWebView &&
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          isCorrect: correct,
          questionUIMode: currentExercise.data.uiMode,
          questionType: currentExercise.type,
          difficulty: currentExercise.difficulty,
          points: awardedPoints,
          noteCount: currentExercise.data.notes?.length || 0,
          moduleType: localModuleType,
        })
      );

    setIsCorrect(correct);
  };

  const renderExercise = (exercise: ExerciseDefinition) => {
    const { type, data } = exercise;
    const uiMode = data.uiMode || "PIANO"; // default to PIANO if not specified

    if (uiMode === "MULTIPLE_CHOICE" && data.choices && data.correctAnswer) {
      return (
        <MultipleChoiceExercise
          ref={currentExerciseRef}
          exercise={exercise}
          prompt={data.promptText || "Select the correct answer:"}
          choices={data.choices}
          correctChoice={data.correctAnswer}
          onResult={handleExerciseResult}
        />
      );
    } else {
      switch (type) {
        case ExerciseType.NOTE_ID:
          return (
            <NoteIdentification
              exercise={exercise}
              ref={currentExerciseRef}
              onResult={handleExerciseResult}
            />
          );
        case ExerciseType.INTERVAL_ID:
          return (
            <IntervalIdentification
              exercise={exercise}
              ref={currentExerciseRef}
              onResult={handleExerciseResult}
            />
          );
        case ExerciseType.CHORD_ID:
          return (
            <ChordIdentification
              exercise={exercise}
              ref={currentExerciseRef}
              onResult={handleExerciseResult}
            />
          );
        case ExerciseType.SCALE_ASCENDING_DESCENDING:
          return (
            <ScaleAscendingDescending
              exercise={exercise}
              ref={currentExerciseRef}
              onResult={handleExerciseResult}
            />
          );
        default:
          return <UnsupportedExercise exercise={exercise} />;
      }
    }
  };

  return (
    <motion.div
      style={{
        display: "flex",
        position: "relative",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(to-br, black, #141414)",
        color: "white",
      }}
      initial={{ opacity: 0 }} // Fade in the whole page
      animate={{ opacity: 1 }}
      transition={{ duration: 0.75 }}
    >
      <Container
        maxW={"container.md"}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        flex={1}
        height={"100%"}
        minHeight={"100vh"}
        pt={4}
        pb={32}
      >
        {currentExercise && (
          <motion.div
            key={currentExercise.id} // Ensure unique key for each exercise to trigger animation
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.75 }}
          >
            {renderExercise(currentExercise)}
          </motion.div>
        )}

        <Flex minHeight={"100px"}></Flex>
      </Container>
      {/* Navigation Buttons */}
      <Flex
        position={"fixed"}
        bottom={0}
        left={0}
        right={0}
        justifyContent="space-between"
        alignItems={"center"}
        width={"100%"}
        p={4}
        backgroundColor={"#141414"}
        borderTop={"2px solid"}
        borderColor={"whiteAlpha.200"}
        borderTopRadius={{ base: "2.5em", md: "3em" }}
        px={{ base: 5, md: 10 }}
        py={hasSubmitted ? 6 : 8}
        pt={5}
        pb={10}
        transition="all 0.2s ease-out"
      >
        {hasSubmitted ? (
          <Flex flexDirection={"row"} gap={{ base: 3, md: 5 }}>
            <IconButton
              aria-label="Icon item"
              icon={
                isCorrect ? (
                  <HiCheck
                    style={{
                      fontSize: isMobile ? "1.3em" : "1.8em",
                      color: "white",
                    }}
                  />
                ) : (
                  <HiOutlineX
                    style={{
                      fontSize: isMobile ? "1.3em" : "1.8em",
                      color: "white",
                    }}
                  />
                )
              }
              borderRadius={"50%"}
              size={"lg"}
              fontSize={"2xl"}
              height={{ base: "50px", md: "70px" }}
              width={{ base: "50px", md: "70px" }}
              colorScheme={isCorrect ? "green" : "red"}
              color={"white"}
              backgroundColor={isCorrect ? "green.600" : "red.600"}
            />
            <Flex flexDirection={"column"}>
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                color={isCorrect ? "green.600" : "red.600"}
                fontWeight={700}
              >
                {" "}
                {isCorrect ? "Great job!" : "Oops, not quite"}
              </Text>
              <Text
                fontSize={"0.95em"}
                color={isCorrect ? "green.600" : "red.600"}
              >
                {!isCorrect ? "" : ""}
              </Text>

              <Button
                size={{ base: "xs", md: "sm" }}
                mt={{ base: 0, md: 2 }}
                ml={-2}
                px={0}
                leftIcon={<FiFlag />}
                color={isCorrect ? "green.600" : "red.600"}
                width={"min-content"}
                variant={"ghost"}
                textTransform={"uppercase"}
                _hover={{
                  backgroundColor: "transparent",
                  color: !isCorrect ? "red.600" : "green.600",
                }}
              >
                Report
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Button
            variant={"outline"}
            colorScheme="whiteAlpha"
            width={"100px"}
            size={"md"}
            fontSize={{ base: "0.75em", md: "0.8em" }}
            fontWeight={800}
            borderRadius={"0.9em"}
            textTransform={"uppercase"}
            color={"whiteAlpha.500"}
            borderColor={"whiteAlpha.300"}
            onClick={() => handleSkip()}
            isDisabled={currentExerciseIndex >= exercises.length - 1}
          >
            Skip
          </Button>
        )}

        <Button
          onClick={() => {
            hasSubmitted ? handleNextExercise() : checkAnswer();
          }}
          colorScheme={hasSubmitted ? (isCorrect ? "green" : "red") : "blue"}
          width={"100px"}
          size={"md"}
          fontSize={{ base: "0.75em", md: "0.8em" }}
          fontWeight={800}
          borderRadius={"0.9em"}
          textTransform={"uppercase"}
          backgroundColor={
            hasSubmitted
              ? isCorrect
                ? "green.600"
                : "red.600"
              : "whiteAlpha.200"
          }
        >
          {hasSubmitted ? "Continue" : "Check"}
        </Button>
      </Flex>
    </motion.div>
  );
};

export default Landing;
